<template>
   <div
      v-if="languages"
      class="mainbar d-flex w-100 align-items-center"
      id="mainbar"
      v-font-color-contrast
   >
      <div class="mainbar-home d-flex">
         <img
            v-if="(logo && $route.name != LOGIN_ROUTE_NAMES.REGISTER && $route.query.step) || (logo && isDesktop)"
            :src="logo"
            alt="logo"
            class="mainbar-logo"
         />
      </div>
      <p
         v-if="isDesktop"
         class="mainbar-title mainbar-title-center d-flex align-items-center fw-bold"
      >
         {{ title }}
      </p>
      <p
         v-else-if="$route.name === JOURNEY_ROUTE_NAMES.JOURNEY || $route.name === JOURNEY_ROUTE_NAMES.JOURNEY_QUESTION"
         class="mainbar-title mainbar-title-center"
      >
         {{ stepTitle }}
      </p>
      <p class="ms-auto m-0 me-3 mainbar-flicker-animation">{{ time }}</p>

      <div
         v-if="languages && journey?.languages?.length > 1"
         class="language"
      >
         <NxtSelect
            :model-value="currentLanguageId"
            name="language_id"
            :has-clear-option="false"
            :options="journey.languages.map((lang: any) => ({ ...lang, code: lang.code.toUpperCase() }))"
            mode="light"
            :display-key="'code'"
            @update:model-value="languageSelect"
         />
      </div>
   </div>
</template>

<script setup lang="ts">
import Variables from '@/core/helpers/variables';
import { NxtSelect } from '@nxtpeople/nxt-styles';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { JOURNEY_ROUTE_NAMES } from '../journeys/routes';
import { LOGIN_ROUTE_NAMES } from '../login/routes';

const variables = new Variables();
const { getters, state, commit, dispatch } = useStore();
const route = useRoute();
const { locale } = useI18n();

const title = ref<string | null>(null);

const logo = computed(() => getters.logo);
const time = computed(() => getters.time);
const journey = computed(() => getters.journey);

const languages = computed(() => state.languages);

const currentLanguageId = computed(() => {
   if (!journey.value || !languages.value) return null;
   const currentLang = languages.value.find((lang: any) => lang.code === locale.value);
   return currentLang ? currentLang.id : null;
});

const stepTitle = computed(() => {
   if (!journey.value || !route.query.step) return;
   return variables.variableToText(journey.value.steps[+route.query.step - 1].name[locale.value]);
});

const isDesktop = computed(() => window.innerWidth > 768);

function languageSelect(newLanguageId: number) {
   const newLanguage = languages.value.find((language: any) => language.id == newLanguageId);
   commit('setLanguage', newLanguage);
   document.title = `NxtForm - ${journey.value.name[locale.value] || journey.value.name[Object.keys(journey.value.name)[0]]}`;
   if (!sessionStorage.getItem('submission_id')) return;
   dispatch('patchSubmission', {
      body: [{ op: 'replace', path: 'language_id', value: newLanguageId }],
   });
}
</script>

<style lang="scss" scoped>
.mainbar {
   position: sticky;
   top: 0;
   left: 0;
   width: 100vw;
   height: var(--header-height) !important;
   z-index: 1000;
   padding: 0 var(--nxt-gutter-large);
   @media (min-width: 769px) {
      height: var(--header-height) !important;
      background-color: black;
      background-color: var(--headerColor);
   }

   &-flicker-animation {
      -webkit-animation: flickerAnimation 3s infinite;
      -moz-animation: flickerAnimation 3s infinite;
      animation: flickerAnimation 3s infinite;
   }

   &-logo {
      height: 26px;

      @media (min-width: 769px) {
         height: 49px;
      }
   }

   &-title {
      font-size: 0.875rem;
      margin: 0;
      display: -webkit-box;
      overflow: hidden;
      -webkit-line-clamp: 2;
      line-clamp: 2;
      -webkit-box-orient: vertical;
      @media (min-width: 769px) {
         font-size: 1.625rem;
      }

      &-center {
         position: absolute;
         left: 50%;
         transform: translateX(-50%);
         max-width: 40%;
         text-align: center;
      }
   }

   &-hidden {
      visibility: hidden !important;
   }
}

.language {
   max-width: toRem(72);
   color: var(--nxt-dark);

   :deep(.nxt-select) {
      .nxt-select-main,
      .nxt-select-main .nxt-select-main__content__value {
         overflow-x: visible;
         padding-right: unset;
      }
      .nxt-select-main__content__search {
         overflow-x: visible;
      }
      input {
         padding-right: unset;
      }
   }
}

@keyframes flickerAnimation {
   0% {
      opacity: 1;
   }
   10% {
      opacity: 0;
   }
   15% {
      opacity: 1;
   }
   100% {
      opacity: 1;
   }
}
@-moz-keyframes flickerAnimation {
   0% {
      opacity: 1;
   }
   10% {
      opacity: 0;
   }
   15% {
      opacity: 1;
   }
   100% {
      opacity: 1;
   }
}
@-webkit-keyframes flickerAnimation {
   0% {
      opacity: 1;
   }
   10% {
      opacity: 0;
   }
   15% {
      opacity: 1;
   }
   100% {
      opacity: 1;
   }
}
</style>

